/**
 * Media Thunks - Redux thunks for media-related operations
 *
 * This file contains all the async thunks related to media operations,
 * separated from the slice file for better organization.
 */

import { createAsyncThunk } from '@reduxjs/toolkit';

import { mediaApiService, type ApiMediaFile } from '../../services/mediaApiService';
import { mediaService } from '../../services/mediaService';
import { MediaType, type MediaFile, type MediaProps, type TabName } from '../../types/common.types';
import { removeSpecialChars } from '../../utils/common';
import { handleApiError } from '../../utils/errorHandling';
import { getGroupedFilesByType } from '../../utils/mockTabFilesFetch';
import type { RootState } from '../types';

// Default cache TTL for media files (24 hours) - 1 hour less than 24 hours to account
// for time to live on the presigned URL
// avoiding repeated audit logging of the same file
export const DEFAULT_MEDIA_CACHE_TTL = 23 * 60 * 60 * 1000;

/**
 * Fetch media files for a specific tab
 */
export const fetchMediaFiles = createAsyncThunk(
  'media/fetchMediaFiles',
  async (tabName: TabName, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const selectedProduct = removeSpecialChars(state.product.selectedProductName.toLowerCase());
      const folder = state.product.tabs.find((tab) => tab.label === tabName)?.folder ?? '';

      // Check if we have cached data for this tab and it's not expired
      const cachedData = state.media.mediaCache[tabName];
      if (cachedData && cachedData.expiresAt > Date.now()) {
        console.log(`Using cached data for tab: ${tabName}`);
        return cachedData.data;
      }

      // Try to fetch real data from API
      try {
        const apiFiles = await mediaApiService.getTabFiles(selectedProduct, folder);

        // If we have API files, process them
        if (apiFiles && apiFiles.length > 0) {
          console.log(`Fetched ${apiFiles.length} files from API for tab: ${tabName}`);
          const processedData = processApiFiles(apiFiles);
          return processedData;
        }
      } catch (error) {
        // Log but don't fail - we'll fall back to mock data
        console.warn('Failed to fetch API files:', error);
      }

      // Fall back to mock data if API call fails or returns no data
      console.log(`Using mock data for tab: ${tabName}`);
      return getGroupedFilesByType(tabName);
    } catch (error) {
      const standardError = handleApiError(error, 'fetchMediaFiles', { tabName });
      return rejectWithValue(standardError.message);
    }
  }
);

/**
 * Fetch a media file by ID
 */
export const fetchMediaFile = createAsyncThunk(
  'media/fetchMediaFile',
  async ({ fileId }: { fileId: string; ttlMs?: number }, { rejectWithValue }) => {
    try {
      // Use the mediaApiService directly to avoid circular dependencies
      const url = await mediaApiService.getFilePresignedUrl(fileId);
      return mediaService.sanitizeUrl(url);
    } catch (error) {
      const standardError = handleApiError(error, 'fetchMediaFile', { fileId });
      return rejectWithValue(standardError.message);
    }
  }
);

/**
 * Process API files into the format expected by the UI
 *
 * @param files - The API files to process
 * @returns The processed files
 *
 * @description
 * IMPORTANT: This logic is temporary and will be updated
 * with concrete fileType handling when backend is ready.
 * TODO: Update this once backend is ready.
 */
export function processApiFiles(files: ApiMediaFile[]): {
  mediaFiles: Record<string, MediaProps[]>;
  textFiles: MediaFile[];
} {
  const mediaFiles: Record<string, MediaProps[]> = {
    video: [],
    audio: [],
    image: [],
    pdf: [],
    text: [],
  };
  const textFiles: MediaFile[] = [];

  // Map specific file types to general categories if needed
  const fileTypeMap: Record<string, string> = {
    mp4: 'video',
  };

  for (const file of files) {
    try {
      if (file.fileType === 'text') {
        // Process text files
        textFiles.push({
          fileType: 'text',
          title: file.fileName,
          description: file.description || '',
          content: file.content || '',
        });
      } else {
        // Map file type if needed, otherwise use the original
        const category = fileTypeMap[file.fileType] || file.fileType;

        // Process media files - dynamically categorize by mapped fileType
        if (!mediaFiles[category] && Object.values(MediaType).includes(category as MediaType)) {
          mediaFiles[category] = [];
        }

        mediaFiles[category].push({
          name: file.fileName,
          createdDate: file.uploadedAt,
          duration: category === 'video' || category === 'audio' ? '00:00' : '-',
          id: file.fileId,
          fileType: file.fileType, // Keep the original file type
          fetchUrl: async () => {
            if (!file.fileId) {
              throw new Error('No file ID provided');
            }
            return mediaService.getMediaFile(file.fileId);
          },
        });
      }
    } catch (error) {
      console.warn(`Unhandled file type: ${file.fileType}`, file, error);
    }
  }

  return { mediaFiles, textFiles };
}
